/**
 * Sources — table of the 165+ publishers, LinkedIn pages, IR portals,
 * associations and internal proprietary sources that feed the Hail Mary
 * intelligence pipeline.
 *
 * Layout uses CSS grid (not the shadcn <Table>) so that the filter bar
 * and the column-header row can share ONE sticky container at the same
 * `top-[54px]` under the Universe tab bar. Sticky-on-thead inside a
 * table wrapper had produced a ghost/blur artefact and, worse, a header
 * that jumped into the middle of the body while scrolling — grid rows
 * with a shared `grid-cols-[…]` template make both problems physically
 * impossible.
 *
 * Every row exposes the full 9-column PDF metadata:
 *   Source Name · Source Type · Company · Region · URL ·
 *   Priority · Monitor · Owner · Status
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Building2, CircleCheck, CircleDot, ExternalLink, Globe,
} from "lucide-react";
import PageHeader from "@/layout/PageHeader";
import { fetchers, qk } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { SourcesSkeleton } from "@/components/PageSkeletons";

// ---------------- Closed-set constants (mirror of Turn BL backend) --------

const ALL_REGIONS = [
  "Global", "North America", "Europe", "Asia", "Oceania",
  "Middle East", "Africa", "Latin America",
];
const ALL_PRIORITIES = ["Critical", "High", "Medium", "Low", "TBD"];
const ALL_STATUSES   = ["Active", "Paused", "Archived", "Watchlist"];

// Grid template — MUST stay in sync between header row and body rows.
// Widths: Source · Type · Company · Region · URL · Priority · Monitor · Owner · Status
const GRID_COLS = "grid-cols-[220px_160px_160px_110px_60px_100px_80px_80px_110px]";
const MIN_W     = "min-w-[1080px]";

// ---------------- Chip color palettes ------------------------------------

const PRIORITY_STYLE = {
  Critical: "bg-red-500/10    text-red-600    border-red-500/30",
  High:     "bg-amber-500/10  text-amber-600  border-amber-500/30",
  Medium:   "bg-slate-500/10  text-slate-600  border-slate-500/30",
  Low:      "bg-zinc-500/10   text-zinc-500   border-zinc-500/30",
  TBD:      "bg-hm-bg-1       text-hm-fg-subtle border-hm-border italic",
};

const STATUS_STYLE = {
  Active:    "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  Paused:    "bg-amber-500/10   text-amber-600   border-amber-500/30",
  Archived:  "bg-zinc-500/10    text-zinc-500    border-zinc-500/30",
  Watchlist: "bg-sky-500/10     text-sky-600     border-sky-500/30",
};

// ---------------- Small building blocks ----------------------------------

function Chip({ children, className, ...props }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 mono text-[10px] uppercase tracking-wider",
        "px-2 py-0.5 rounded border whitespace-nowrap",
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={cn(
        "text-[12px] font-medium rounded-full px-3 py-1 border transition-all",
        active
          ? "bg-hm-fg text-hm-bg border-hm-fg"
          : "bg-hm-bg text-hm-fg-muted border-hm-border hover:text-hm-fg hover:border-hm-border-strong",
      )}
    >
      {children}
    </button>
  );
}

function Favicon({ domain }) {
  const [failed, setFailed] = useState(false);
  if (failed || !domain) {
    return (
      <div
        className="flex items-center justify-center rounded-md border border-hm-border bg-hm-bg-1 shrink-0"
        style={{ width: 22, height: 22 }}
      >
        <Globe className="h-3 w-3 text-hm-fg-subtle" />
      </div>
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
      alt=""
      width={22} height={22}
      loading="lazy"
      onError={() => setFailed(true)}
      style={{
        width: 22, height: 22, borderRadius: 6,
        border: "1px solid var(--hm-border)",
        background: "var(--hm-bg-elev-2)",
        objectFit: "cover", flexShrink: 0,
      }}
    />
  );
}

// Column headers definition — mirrors the grid template above 1-for-1.
const COLS = [
  { key: "source",   label: "Source" },
  { key: "type",     label: "Type" },
  { key: "company",  label: "Company" },
  { key: "region",   label: "Region" },
  { key: "url",      label: "URL",      center: true },
  { key: "priority", label: "Priority" },
  { key: "monitor",  label: "Monitor",  center: true },
  { key: "owner",    label: "Owner" },
  { key: "status",   label: "Status" },
];

// ---------------- Page ---------------------------------------------------

export default function SourcesPage({ hideHeader = false, externalQ } = {}) {
  const { data, isPending } = useQuery({
    queryKey: qk.sources(),
    queryFn: fetchers.sources,
  });

  // ---- Client-side filter state ----
  const [localQ, setLocalQ] = useState("");
  const q = externalQ !== undefined ? externalQ : localQ;
  const setQ = externalQ !== undefined ? () => {} : setLocalQ;
  const [typeFilter, setTypeFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const all = data?.data || [];

  const distinctTypes = useMemo(() => {
    const s = new Set();
    for (const r of all) if (r.source_kind) s.add(r.source_kind);
    return Array.from(s).sort();
  }, [all]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return all.filter((s) => {
      if (typeFilter !== "all" && (s.source_kind || "") !== typeFilter) return false;
      if (regionFilter !== "all" && (s.region || "") !== regionFilter) return false;
      if (priorityFilter !== "all" && (s.source_priority || "") !== priorityFilter) return false;
      if (statusFilter !== "all" && (s.status || "") !== statusFilter) return false;
      if (ql) {
        const hay = `${s.name || ""} ${s.company_name || ""} ${s.domain || ""} ${s.url || ""}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [all, q, typeFilter, regionFilter, priorityFilter, statusFilter]);

  return (
    <div data-testid="sources-page">
      {!hideHeader && (
        <PageHeader title="Sources" subtitle="Publication universe." />
      )}

      {isPending || !data ? (
        <SourcesSkeleton />
      ) : (
        <>
          {/* Sticky container — filter bar + header row share ONE sticky
              parent so they never overlap and no gap lets scrolling
              content bleed through. Fully opaque, zero backdrop-blur. */}
          <div
            className="sticky top-[54px] z-20 -mx-8 bg-hm-bg"
            data-testid="sources-sticky"
          >
            {/* Filter bar */}
            <div
              className="px-8 pt-3 pb-3 space-y-3"
              data-testid="sources-filters"
            >
              {/* Row 1 — Source Type dropdown + counter + search */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mr-1">
                  Source Type
                </span>
                <select
                  data-testid="sources-filter-type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="text-[12px] font-medium rounded-full px-3 py-1 border border-hm-border bg-hm-bg text-hm-fg-muted"
                >
                  <option value="all">All ({all.length})</option>
                  {distinctTypes.map((t) => {
                    const n = all.filter((s) => s.source_kind === t).length;
                    return <option key={t} value={t}>{t} ({n})</option>;
                  })}
                </select>

                <div className="ml-auto flex items-center gap-3">
                  <span
                    className="text-xs text-hm-fg-subtle whitespace-nowrap"
                    data-testid="sources-count"
                  >
                    Showing {filtered.length} of {all.length} sources
                  </span>
                </div>
              </div>

              {/* Row 2 — Region chips */}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mr-1">
                  Region
                </span>
                <FilterChip
                  active={regionFilter === "all"}
                  onClick={() => setRegionFilter("all")}
                  testid="sources-filter-region-all"
                >
                  All
                </FilterChip>
                {ALL_REGIONS.map((r) => {
                  const n = all.filter((s) => s.region === r).length;
                  if (n === 0) return null;
                  return (
                    <FilterChip
                      key={r}
                      active={regionFilter === r}
                      onClick={() => setRegionFilter(r)}
                      testid={`sources-filter-region-${r.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {r} <span className="mono text-[10px] ml-1 opacity-70">{n}</span>
                    </FilterChip>
                  );
                })}
              </div>

              {/* Row 3 — Priority + Status chips */}
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mr-1">
                    Priority
                  </span>
                  <FilterChip
                    active={priorityFilter === "all"}
                    onClick={() => setPriorityFilter("all")}
                    testid="sources-filter-priority-all"
                  >
                    All
                  </FilterChip>
                  {ALL_PRIORITIES.map((p) => {
                    const n = all.filter((s) => s.source_priority === p).length;
                    return (
                      <FilterChip
                        key={p}
                        active={priorityFilter === p}
                        onClick={() => setPriorityFilter(p)}
                        testid={`sources-filter-priority-${p.toLowerCase()}`}
                      >
                        {p} <span className="mono text-[10px] ml-1 opacity-70">{n}</span>
                      </FilterChip>
                    );
                  })}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mr-1">
                    Status
                  </span>
                  <FilterChip
                    active={statusFilter === "all"}
                    onClick={() => setStatusFilter("all")}
                    testid="sources-filter-status-all"
                  >
                    All
                  </FilterChip>
                  {ALL_STATUSES.map((st) => {
                    const n = all.filter((s) => s.status === st).length;
                    if (n === 0) return null;
                    return (
                      <FilterChip
                        key={st}
                        active={statusFilter === st}
                        onClick={() => setStatusFilter(st)}
                        testid={`sources-filter-status-${st.toLowerCase()}`}
                      >
                        {st} <span className="mono text-[10px] ml-1 opacity-70">{n}</span>
                      </FilterChip>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Column-header row (grid) — attached directly to the
                filter bar so the two never separate visually. */}
            <div className="bg-hm-bg-1 border-y border-hm-border">
              <div className={cn("px-8 grid gap-0 h-10 items-center", GRID_COLS, MIN_W)}>
                {COLS.map((c) => (
                  <div
                    key={c.key}
                    className={cn(
                      "px-3 text-[11px] uppercase tracking-wider text-hm-fg-muted font-medium",
                      c.center && "text-center",
                    )}
                  >
                    {c.label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Body */}
          {filtered.length === 0 ? (
            <div
              className="mono text-hm-fg-subtle text-center py-12 text-xs uppercase tracking-widest border-b border-hm-border/60 -mx-8"
            >
              No sources match the current filters
            </div>
          ) : (
            <div className="-mx-8" data-testid="sources-table-wrapper">
              <div className={MIN_W}>
                {filtered.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`source-row-${s.domain || s.id}`}
                    className={cn(
                      "px-8 grid gap-0 items-center border-b border-hm-border/60 hover:bg-hm-bg-1/60 transition-colors",
                      GRID_COLS,
                    )}
                  >
                    {/* Source Name */}
                    <div className="px-3 py-2.5 min-w-0" title={s.name}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Favicon domain={s.domain} />
                        <span className="text-hm-fg font-medium text-sm truncate">
                          {s.name}
                        </span>
                      </div>
                    </div>

                    {/* Source Type */}
                    <div className="px-3 py-2.5 min-w-0">
                      {s.source_kind ? (
                        <Chip className="border-hm-border bg-hm-bg-1 text-hm-fg-muted">
                          {s.source_kind}
                        </Chip>
                      ) : (
                        <span className="text-hm-fg-subtle text-xs">—</span>
                      )}
                    </div>

                    {/* Company */}
                    <div
                      className="px-3 py-2.5 min-w-0"
                      title={s.company_name || ""}
                    >
                      {s.company_name ? (
                        <div className="flex items-center gap-1.5 min-w-0">
                          {s.company_id ? (
                            <Building2
                              className="h-3.5 w-3.5 text-hm-accent shrink-0"
                              aria-label="Linked company"
                            />
                          ) : (
                            <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          )}
                          <span
                            className={cn(
                              "text-sm truncate",
                              s.company_id ? "text-hm-fg" : "text-hm-fg-muted",
                            )}
                          >
                            {s.company_name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-hm-fg-subtle text-xs">—</span>
                      )}
                    </div>

                    {/* Region */}
                    <div className="px-3 py-2.5 min-w-0">
                      {s.region ? (
                        <Chip className="border-hm-border bg-hm-bg-1 text-hm-fg-muted">
                          {s.region}
                        </Chip>
                      ) : (
                        <span className="text-hm-fg-subtle text-xs">—</span>
                      )}
                    </div>

                    {/* URL — icon only, title tooltip = domain */}
                    <div className="px-3 py-2.5 text-center">
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={s.domain || s.url}
                          data-testid={`source-link-${s.domain || s.id}`}
                          className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-hm-border bg-hm-bg text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong transition-colors"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <span className="text-hm-fg-subtle text-xs">—</span>
                      )}
                    </div>

                    {/* Priority */}
                    <div className="px-3 py-2.5 min-w-0">
                      {s.source_priority ? (
                        <Chip
                          className={PRIORITY_STYLE[s.source_priority] || "border-hm-border bg-hm-bg-1 text-hm-fg-muted"}
                          data-testid={`source-priority-${s.source_priority.toLowerCase()}`}
                        >
                          {s.source_priority}
                        </Chip>
                      ) : (
                        <span className="text-hm-fg-subtle text-xs">—</span>
                      )}
                    </div>

                    {/* Monitor */}
                    <div className="px-3 py-2.5 text-center">
                      {s.monitor === false ? (
                        <CircleDot
                          className="inline h-4 w-4 text-hm-fg-subtle"
                          aria-label="Not monitored"
                        />
                      ) : (
                        <CircleCheck
                          className="inline h-4 w-4 text-emerald-500"
                          aria-label="Monitored"
                        />
                      )}
                    </div>

                    {/* Owner */}
                    <div className="px-3 py-2.5 min-w-0">
                      {s.owner ? (
                        <span
                          className={cn(
                            "text-sm truncate block",
                            s.owner === "TBC"
                              ? "italic text-hm-fg-subtle"
                              : "text-hm-fg-muted",
                          )}
                        >
                          {s.owner}
                        </span>
                      ) : (
                        <span className="text-hm-fg-subtle text-xs">—</span>
                      )}
                    </div>

                    {/* Status */}
                    <div className="px-3 py-2.5 min-w-0">
                      {s.status ? (
                        <Chip
                          className={STATUS_STYLE[s.status] || "border-hm-border bg-hm-bg-1 text-hm-fg-muted"}
                          data-testid={`source-status-${s.status.toLowerCase()}`}
                        >
                          {s.status}
                        </Chip>
                      ) : (
                        <span className="text-hm-fg-subtle text-xs">—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
